import { createContext } from '@shared/utils/createContext';
import { callAllHandlers } from '@shared/utils/functions';
import { useControllableState } from '@shared/utils/hooks/useControllableState';
import { useCallback } from 'react';
import { useDescendant, useDescendants } from './useDescendants';
import type { AccordionProps, AccordionItemProps } from './Accordion.types';
import { addItem, removeItem } from '@shared/utils/array';
import { isArray } from '@shared/utils/assertions';
import { useIds } from '@withjoy/joykit/hooks';
import type { PropGetter } from '@withjoy/joykit/utils';
import { mergeRefs } from '@shared/utils/hooks/setRef';

//---------------------------------------
// Accordion

type AccordionContext = Omit<ReturnType<typeof useAccordion>, 'descendants' | 'htmlProps'>;
const [AccordionProvider, useAccordionContext] = createContext<AccordionContext>({ name: 'AccordionProvider' });

export const useAccordion = (props: AccordionProps) => {
  const { defaultIndex, enableMulti, index: indexProp, onChange: onChangeProp, ...htmlProps } = props;

  const [expandedIndices, setExpandedIndices] = useControllableState({
    value: indexProp,
    defaultValue: () => {
      if (enableMulti) {
        return defaultIndex ?? [];
      }
      return defaultIndex ?? -1;
    },
    changeHandler: onChangeProp as (idx: number | number[]) => void,
  });

  const descendants = useDescendants();

  const getAccordionItemProps = useCallback(
    (idx: number | null) => {
      let isOpen = false;

      if (idx !== null) {
        isOpen = isArray(expandedIndices) ? expandedIndices.includes(idx) : expandedIndices === idx;
      }

      const onToggle = (isOpen: boolean) => {
        if (idx === null) {
          return;
        }
        if (enableMulti && isArray(expandedIndices)) {
          const nextIndex: number[] = isOpen ? addItem(expandedIndices, idx) : removeItem(expandedIndices, idx);
          setExpandedIndices(nextIndex);
        } else if (isOpen) {
          setExpandedIndices(idx);
        } else {
          setExpandedIndices(-1);
        }
      };

      return {
        isOpen,
        onToggle,
      } as const;
    },
    [expandedIndices, setExpandedIndices, enableMulti],
  );

  return {
    htmlProps,
    descendants,
    getAccordionItemProps,
  };
};

export { AccordionProvider };

//---------------------------------------
// Accordion Item

interface AccordionItemContext extends Omit<ReturnType<typeof useAccordionItem>, 'htmlProps'> {}
const [AccordionItemProvider, useAccordionItemContext] = createContext<AccordionItemContext>({ name: 'AccordionItemProvider' });
export const useAccordionItem = (props: AccordionItemProps) => {
  // `isOpen`/`onOpenChange` are ours, not the DOM's — keep them out of `htmlProps`, which is spread
  // straight onto the item element.
  const { isOpen: isOpenProp, onOpenChange, ...htmlProps } = props;
  const { id } = props;
  const { getAccordionItemProps } = useAccordionContext();
  const { index, register } = useDescendant();

  const { isOpen: isOpenByIndex, onToggle: onToggleByIndex } = getAccordionItemProps(index === -1 ? null : index);

  // Per-item control wins when the consumer opts in (see AccordionItemProps.isOpen). The descendant
  // index is still registered — it just stops deciding this item's open state, which is the point:
  // indices are only reassigned on (un)register, so they are wrong for a list that re-orders keyed
  // items in place.
  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : isOpenByIndex;

  const [buttonId, panelId] = useIds(id, 'accordion-button', 'accordion-panel');

  const onClick = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(!isOpen);
      return;
    }
    onToggleByIndex(!isOpen);
  }, [isControlled, onOpenChange, isOpen, onToggleByIndex]);

  const getButtonProps = useCallback<PropGetter<HTMLButtonElement>>(
    (props = {}, ref) => {
      return {
        ...props,
        id: buttonId,
        'aria-expanded': isOpen,
        'aria-controls': panelId,
        onClick: callAllHandlers(props.onClick, onClick),
        ref: mergeRefs(register, ref),
      };
    },
    [buttonId, panelId, isOpen, onClick, register],
  );

  const getPanelProps = useCallback<PropGetter<HTMLDivElement>>(
    props => {
      return {
        ...props,
        id: panelId,
        'aria-labelledby': buttonId,
        role: 'region',
      };
    },
    [buttonId, panelId],
  );

  return {
    isOpen,
    htmlProps,
    getButtonProps,
    getPanelProps,
  };
};

export { AccordionItemProvider, useAccordionItemContext };
